/**
 * Esquemas de escritura del dominio Inquilinos.
 *
 * La validación del documento de identidad es la razón de que este fichero viva en `shared`:
 * el usuario tiene que ver «la letra de control debería ser Z» mientras teclea, y el servidor
 * tiene que rechazar exactamente lo mismo aunque alguien salte el formulario.
 */
import { z } from 'zod';
import {
  zEmail,
  zIsoDate,
  zOptionalText,
  zPhone,
  zText,
  zUuid,
} from '../validation/primitives';
import { idDocumentError, normalizeIdDocument } from '../validation/identity';
import { ibanError, normalizeIban } from '../validation/iban';
import { tenantRoomSchema } from './lease';

export const ID_DOCUMENT_TYPES = ['dni', 'nie', 'pasaporte'] as const;

/**
 * Los papeles que pide la ficha del inquilino, en el orden en que se enseñan. Son también las
 * únicas categorías que se pueden subir desde ella: una factura o una escritura no son de nadie.
 */
export const TENANT_DOCUMENT_CATEGORIES = [
  'Identidad',
  'Pasaporte',
  'Contratos',
  'Renovacion',
  'Rescision',
  'Nominas',
  'Auditoria',
  'Otros',
] as const;

export type TenantDocumentCategory = (typeof TENANT_DOCUMENT_CATEGORIES)[number];

/**
 * Los dos papeles que no se guardan sin más: se LEEN y cambian el contrato vigente. La
 * renovación lo alarga hasta la fecha que pone; la rescisión lo termina ese día.
 */
export const LEASE_CHANGE_CATEGORIES = ['Renovacion', 'Rescision'] as const;

export type LeaseChangeCategory = (typeof LEASE_CHANGE_CATEGORIES)[number];

export function isLeaseChangeCategory(category: string): category is LeaseChangeCategory {
  return (LEASE_CHANGE_CATEGORIES as readonly string[]).includes(category);
}

export const TENANT_DOCUMENT_LABELS: Record<TenantDocumentCategory, string> = {
  Identidad: 'Identidad',
  Pasaporte: 'Pasaporte',
  Contratos: 'Contrato',
  Renovacion: 'Renovación de contrato',
  Rescision: 'Rescisión de contrato',
  Nominas: 'Nóminas',
  Auditoria: 'Registro de auditoría',
  Otros: 'Otros',
};
export const PREFERRED_LANGUAGES = ['es', 'en', 'ca', 'val'] as const;

export const PREFERRED_LANGUAGE_LABELS: Record<
  (typeof PREFERRED_LANGUAGES)[number],
  string
> = {
  es: 'Español',
  en: 'Inglés',
  ca: 'Catalán',
  val: 'Valenciano',
};

const tenantBase = z.object({
  fullName: zText(160, 'El nombre completo'),
  idDocumentType: z.enum(ID_DOCUMENT_TYPES),
  /**
   * Se normaliza SIEMPRE antes de validar y de guardar (mayúsculas, sin guiones ni espacios).
   * Sin esto, `12345678-z` y `12345678Z` son dos filas distintas y el único
   * `(organization_id, id_document)` no detecta el duplicado.
   */
  idDocument: z
    .string()
    .trim()
    .transform(normalizeIdDocument)
    .refine((value) => value === '' || value.length <= 20, 'Documento demasiado largo.')
    .nullable()
    .optional(),
  nationality: zOptionalText(80),
  birthDate: zIsoDate.nullable().optional(),
  email: zEmail.nullable().optional(),
  phone: zPhone.nullable().optional(),

  idDocumentExpiresAt: zIsoDate.nullable().optional(),

  emergencyContactName: zOptionalText(160),
  emergencyContactPhone: zPhone.nullable().optional(),
  emergencyContactRelationship: zOptionalText(80),

  /**
   * La cuenta entera, la que pone su contrato. Se guarda normalizada (sin espacios, en
   * mayúsculas) y con su dígito de control comprobado. De ella sale `ibanLast4`, que es lo que
   * usa el extracto para reconocer sus pagos.
   */
  iban: z
    .string()
    .trim()
    .transform(normalizeIban)
    .superRefine((value, ctx) => {
      const error = ibanError(value);
      if (error) ctx.addIssue({ code: z.ZodIssueCode.custom, message: error });
    })
    .nullable()
    .optional(),
  /**
   * Solo la terminación, para quien no tiene la cuenta entera a mano. Si viaja `iban`, se
   * deriva de él y esto se ignora.
   */
  ibanLast4: z
    .string()
    .trim()
    .regex(/^\d{4}$/, 'Son los 4 últimos dígitos del IBAN.')
    .nullable()
    .optional(),
  mandateReference: zOptionalText(60),
  mandateSignedAt: zIsoDate.nullable().optional(),

  preferredLanguage: z.enum(PREFERRED_LANGUAGES).optional(),
  marketingConsent: z.boolean().optional(),

  notes: zOptionalText(4000),
});

/**
 * El dígito de control depende del TIPO, así que solo se puede comprobar cuando ya conocemos
 * ambos campos. Por eso va en un `superRefine` del objeto y no en el campo.
 */
function documentCoherent(
  value: { idDocumentType?: string; idDocument?: string | null },
  ctx: z.RefinementCtx,
): void {
  if (!value.idDocument || !value.idDocumentType) return;
  const error = idDocumentError(
    value.idDocumentType as (typeof ID_DOCUMENT_TYPES)[number],
    value.idDocument,
  );
  if (error) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['idDocument'], message: error });
  }
}

/** El mandato SEPA sin referencia (o al revés) es un mandato que no se puede presentar. */
function mandateCoherent(
  value: { mandateReference?: string | null; mandateSignedAt?: string | null },
  ctx: z.RefinementCtx,
): void {
  if (value.mandateSignedAt && !value.mandateReference) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['mandateReference'],
      message: 'Un mandato firmado necesita su referencia.',
    });
  }
}

/**
 * El alta puede traer ya DÓNDE vive (`room`): el inquilino y su contrato en borrador se crean
 * en la misma petición, así que una habitación que no se puede asignar no deja a medias el alta.
 * La edición no lo acepta: cambiar de habitación es un contrato nuevo, no un dato de la ficha.
 */
export const createTenantSchema = tenantBase
  .extend({ room: tenantRoomSchema.optional() })
  .superRefine((value, ctx) => {
    documentCoherent(value, ctx);
    mandateCoherent(value, ctx);
  });

export const updateTenantSchema = tenantBase.partial().superRefine((value, ctx) => {
  documentCoherent(value, ctx);
  mandateCoherent(value, ctx);
});

// ---------------------------------------------------------------------------
// Notas
// ---------------------------------------------------------------------------

export const createTenantNoteSchema = z.object({
  body: zText(4000, 'La nota'),
  pinned: z.boolean().optional(),
});

export const updateTenantNoteSchema = createTenantNoteSchema.partial();

// ---------------------------------------------------------------------------
// Acceso al portal
// ---------------------------------------------------------------------------

/**
 * Borrar del todo a un inquilino dado de baja. Se escribe su nombre, y el servidor lo vuelve a
 * comprobar: es lo único de la aplicación que destruye contratos y cobros.
 */
export const purgeTenantSchema = z.object({ confirmation: z.string().trim().min(1, 'Escribe el nombre.') });

export const portalInviteSchema = z.object({
  /** Si el inquilino ya tiene email en la ficha se reutiliza; si no, se pide aquí. */
  email: zEmail.optional(),
});

// ---------------------------------------------------------------------------
// Filtros del listado
// ---------------------------------------------------------------------------

export const TENANT_PAYMENT_STATUSES = ['al_dia', 'pendiente', 'impago'] as const;
export const TENANT_LEASE_FILTERS = ['activo', 'por_vencer', 'historico'] as const;

export const TENANT_SORT_FIELDS = ['fullName', 'placement', 'monthlyRent', 'leaseEndDate'] as const;

export const tenantListQuerySchema = z.object({
  q: z.string().trim().max(120).optional(),
  status: z.enum(TENANT_PAYMENT_STATUSES).optional(),
  property: zUuid.optional(),
  lease: z.enum(TENANT_LEASE_FILTERS).optional(),
  historic: z
    .enum(['true', 'false'])
    .default('false')
    .transform((value) => value === 'true'),
  sort: z.enum(TENANT_SORT_FIELDS).optional(),
  dir: z.enum(['asc', 'desc']).default('asc'),
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(200).default(25),
});

export type CreateTenantInput = z.input<typeof createTenantSchema>;
export type UpdateTenantInput = z.input<typeof updateTenantSchema>;
export type CreateTenantNoteInput = z.input<typeof createTenantNoteSchema>;
export type TenantListQuery = z.input<typeof tenantListQuerySchema>;
